import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { useDropdownAnchor } from './useDropdownAnchor';
import { FLOW_PORTAL_PROPS } from '../useClickOutside';
import { usePeople } from '../usePeople';
import { filterPeopleByName, usePersonSearch } from './usePersonSearch';
import { ME_TOKEN, type FilterCondition } from '@shared/flow/filter';
import type { FlowIdentity } from '@shared/flow/identity';
import type { FlowPeopleScope } from '@shared/flow/people';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

const VALUE_INPUT_CLASS =
  'w-full px-2 py-1 text-[11.5px] border border-secondary dark:border-nightshade-700 rounded-md bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100 placeholder:text-content-placeholder focus:outline-none focus:ring-1 focus:ring-content-action';

const ME_CHIP_CLASS =
  'px-1 rounded bg-bg-blue-light text-content-action dark:text-blueberry-400 text-[10.5px] font-semibold shrink-0';

/**
 * The `{me}`-aware person value picker for `type: 'person'` filter
 * conditions (plan Decision 6, prototype `personPop()` ~line 723). Pinned
 * "You" section stores the literal `ME_TOKEN` ('{me}') string when picked;
 * everyone else (from `usePeople`, A→Z per the endpoint contract) stores
 * their literal name — exactly the existing text-equals semantics for a
 * literal person value.
 *
 * A stored `{me}` value ALWAYS renders as a `[Me]` chip, never a resolved
 * name — the whole point of the token is that a shared saved view resolves
 * per-viewer. Viewer resolution (`→ <sfDisplayName>`) renders alongside the
 * chip from the `identity` prop, purely for the viewer's own orientation.
 *
 * On a people-endpoint failure, degrades to a plain literal text input
 * (never an empty, dead dropdown) — the value is still a plain name string
 * either way, so the write path is unaffected. QA-2 REVERSAL: that fallback
 * input used to debounce 250ms like `FilterPopover`'s other typed-value
 * controls (F9, via the shared `useDebouncedFilterDraft`, F18
 * flush-on-unmount); it's now a plain controlled input instead — `value`
 * comes straight from `FilterPopover`'s staged draft, and every keystroke
 * writes back into that same draft via `onSelect`, exactly like
 * `FreeTextValueInput`. There's nothing left to flush on unmount: closing
 * the popover before "Apply filters" is the discard gesture now, so a value
 * typed here that never reached Apply is correctly dropped, not resurrected.
 *
 * U9: a search input in the "Everyone else" section client-side filters the
 * `usePeople(scope)` list by case-insensitive substring match on `name`
 * (`filterPeopleByName`, shared with `PersonMultiValuePicker` via
 * `./usePersonSearch`) — the pinned "You" row is unaffected by search text,
 * it always renders first regardless. The search text resets whenever the
 * dropdown is reopened (`usePersonSearch`'s `open`-keyed reset), so a search
 * from a prior open never carries into the next one. `scope` (optional)
 * narrows the directory to a `FilterFieldCatalogEntry.peopleScope` variant
 * instead of the full unscoped directory — see that field's doc comment for
 * the business invariant each scope encodes.
 */
export interface PersonValuePickerProps {
  value: FilterCondition['value'];
  identity?: FlowIdentity | null;
  onSelect: (value: string) => void;
  /**
   * Which `/api/flow/people?scope=` variant to query (U9, Owner/ClayDR
   * filter picker) — sourced from the field's catalog entry
   * (`FilterFieldCatalogEntry.peopleScope`, `src/shared/flow/filter-fields.ts`).
   * Omitted → the full unscoped directory (unchanged default behavior).
   */
  scope?: FlowPeopleScope;
}

function PersonFallbackInput({
  value, onSelect,
}: { value: string; onSelect: (v: string) => void }) {
  return (
    <input
      {...AUTOFILL_IGNORE_PROPS}
      type="text"
      value={value}
      onChange={e => onSelect(e.target.value)}
      placeholder="Person name…"
      className={VALUE_INPUT_CLASS}
    />
  );
}

export function PersonValuePicker({ value, identity, onSelect, scope }: PersonValuePickerProps) {
  const { open, setOpen, triggerRef, menuRef, menuStyle } = useDropdownAnchor();
  const peopleQuery = usePeople(scope);
  const [search, setSearch] = usePersonSearch(open);

  const isMe = value === ME_TOKEN;
  const literalValue = !isMe && typeof value === 'string' && value ? value : null;

  if (peopleQuery.isError) {
    return <PersonFallbackInput value={literalValue ?? ''} onSelect={onSelect} />;
  }

  const visiblePeople = filterPeopleByName(peopleQuery.data?.people ?? [], search);

  return (
    <div className="relative" ref={triggerRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        className="flex items-center gap-1 w-full px-2 py-1 text-[11.5px] text-left border border-secondary dark:border-nightshade-700 rounded-md bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100 hover:bg-bg-secondary dark:hover:bg-nightshade-700"
      >
        {isMe ? (
          <span className="flex items-center gap-1 truncate flex-1">
            <span className={ME_CHIP_CLASS}>Me</span>
            {identity?.sfDisplayName && (
              <span className="truncate text-content-tertiary dark:text-nightshade-500">→ {identity.sfDisplayName}</span>
            )}
          </span>
        ) : (
          <span className="truncate flex-1">
            {literalValue ?? <span className="text-content-placeholder">Choose…</span>}
          </span>
        )}
        <ChevronDown className="w-3 h-3 shrink-0 text-content-tertiary dark:text-nightshade-500" />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          data-testid="person-picker-menu"
          {...FLOW_PORTAL_PROPS}
          style={menuStyle}
          className="w-[250px] rounded-lg border border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-800 shadow-lg overflow-hidden py-1"
        >
          <div className="px-2.5 py-1 text-[10px] uppercase tracking-wide font-medium text-content-tertiary dark:text-nightshade-500">You</div>
          <button
            type="button"
            onClick={() => { onSelect(ME_TOKEN); setOpen(false); }}
            className={`flex items-center gap-1.5 w-full text-left px-2.5 py-1 text-[12px] ${
              isMe
                ? 'bg-bg-blue-light text-content-action dark:text-blueberry-400 font-medium'
                : 'text-content-primary dark:text-nightshade-100 hover:bg-bg-secondary dark:hover:bg-nightshade-700'
            }`}
          >
            <span className={ME_CHIP_CLASS}>Me</span>
            {identity?.sfDisplayName && <span>{identity.sfDisplayName}</span>}
          </button>
          <div className="my-1 border-t border-secondary dark:border-nightshade-700" />
          <div className="px-2.5 pb-1">
            <input
              {...AUTOFILL_IGNORE_PROPS}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full px-2 py-1 text-[11.5px] border border-secondary dark:border-nightshade-700 rounded-md bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100 placeholder:text-content-placeholder focus:outline-none focus:ring-1 focus:ring-content-action"
            />
          </div>
          <div className="px-2.5 py-1 text-[10px] uppercase tracking-wide font-medium text-content-tertiary dark:text-nightshade-500">Everyone else</div>
          {peopleQuery.isLoading && (
            <div className="px-3 py-2 text-[12px] text-content-tertiary dark:text-nightshade-500">Loading…</div>
          )}
          {visiblePeople.map(p => (
            <button
              key={p.sfUserId}
              type="button"
              onClick={() => { onSelect(p.name); setOpen(false); }}
              className={`block w-full text-left px-2.5 py-1 text-[12px] ${
                p.name === literalValue
                  ? 'bg-bg-blue-light text-content-action dark:text-blueberry-400 font-medium'
                  : 'text-content-primary dark:text-nightshade-100 hover:bg-bg-secondary dark:hover:bg-nightshade-700'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
